import { TextInput } from "risa-oasis-ui_v2";
import DownloadIcon from "../../../svg/download-icon";

interface SendToAppealLetterViewProps {
  appealLetterText: string;
  canDownload: boolean;
  onDownloadClick: () => void;
  /** Numeric appeal success probability from generate-appeal `details`. */
  appealProbability?: number | null;
  /** Current value of the appeal reason text field. */
  appealReason: string;
  /** Called when the appeal reason text field changes. */
  onAppealReasonChange: (value: string) => void;
}

/**
 * Formats the appeal probability as a percentage for the tag above the letter.
 *
 * @param probability - Numeric probability from the API
 * @returns Percent label (e.g. `75%`), or null when probability is missing
 */
const formatProbabilityLabel = (
  probability?: number | null,
): string | null => {
  if (probability == null || Number.isNaN(probability)) {
    return null;
  }

  return `${probability}%`;
};

/**
 * Appeal-eligible letter preview for the Send to Appeal modal.
 * Shows appeal probability above the letter and an appeal reason input.
 * Modal title and footer actions are provided by parent Modal props.
 *
 * @param props - Letter content, probability, reason field, and download handler
 * @returns Appeal reason field, probability tag, and scrollable letter panel
 */
const SendToAppealLetterView = ({
  appealLetterText,
  canDownload,
  onDownloadClick,
  appealProbability,
  appealReason,
  onAppealReasonChange,
}: SendToAppealLetterViewProps) => {
  const probabilityLabel = formatProbabilityLabel(appealProbability);

  return (
    <div className="flex flex-col gap-4">
      <TextInput
        key="send-to-appeal-reason"
        id="send-to-appeal-reason"
        label="Appeal Reason"
        placeholder="Enter appeal reason"
        defaultValue={appealReason}
        onChange={(event) => onAppealReasonChange(event.value ?? "")}
      />

      {probabilityLabel && (
        <div className="flex items-center gap-2">
          <span className="text-h12 font-semibold text-primaryGray-7">
            Appeal Probability
          </span>
          <span
            className="inline-flex w-fit items-center rounded bg-primaryGray-16 px-2 py-0.5 text-x-tiny font-semibold text-primaryGray-1"
            title={probabilityLabel}
          >
            {probabilityLabel}
          </span>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-primaryGray-15">
        <div className="flex items-center justify-between border-b border-primaryGray-15 bg-primaryGray-16 px-4 py-3">
          <span className="text-h12 font-semibold text-primaryGray-7">
            Appeal Letter
          </span>
          <button
            type="button"
            className="inline-flex items-center gap-1 border-0 bg-transparent p-0 text-tiny font-bold text-tertiaryBlue-4 disabled:cursor-default disabled:opacity-50"
            onClick={onDownloadClick}
            disabled={!canDownload}
          >
            <DownloadIcon />
            <span>Download</span>
          </button>
        </div>

        <div className="min-h-72 max-h-96 overflow-y-auto bg-white p-4">
          <p className="m-0 whitespace-pre-wrap text-h12 leading-relaxed text-primaryGray-1">
            {appealLetterText}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SendToAppealLetterView;
